import type { WorkflowActivity, WorkflowActivityChange, WorkflowFieldDefinition } from '../model';
import { formatTimestampDateKey, timestampTimeZone } from '../../../shared/formatTimestampDate';

export function formatWorkflowActivityDay(value: string, now = new Date()) {
  const key = formatTimestampDateKey(value);
  if (!key) return '날짜 미상';
  const today = formatTimestampDateKey(now.toISOString());
  if (key === today) return '오늘';
  if (key === formatTimestampDateKey(new Date(now.getTime() - 86_400_000).toISOString())) return '어제';
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: timestampTimeZone,
    year: key.slice(0, 4) === today.slice(0, 4) ? undefined : 'numeric',
    month: 'long', day: 'numeric', weekday: 'short',
  }).format(new Date(value));
}

export function formatWorkflowActivityTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('ko-KR', { timeZone: timestampTimeZone, timeStyle: 'short' }).format(date);
}

export type WorkflowActivityFilter = 'all' | 'comments' | 'history';

export type WorkflowActivityValueContext = {
  fields?: readonly WorkflowFieldDefinition[];
  people?: readonly { personId: string; name: string }[];
  organizations?: readonly { id: string; name: string }[];
};

export function filterWorkflowActivities(activities: readonly WorkflowActivity[], filter: WorkflowActivityFilter): WorkflowActivity[] {
  if (filter === 'comments') return activities.filter((activity) => activity.kind === 'COMMENT');
  if (filter === 'history') return activities.filter((activity) => activity.kind !== 'COMMENT');
  return [...activities];
}

/** 같은 밀리초의 승인과 적용 기록도 최초 조회·새로고침 순서에 영향을 받지 않습니다. */
export function chronologicalWorkflowActivities(activities: readonly WorkflowActivity[]): WorkflowActivity[] {
  const timestamp = (value: string) => Date.parse(value) || 0;
  return [...activities].sort((left, right) => timestamp(left.occurredAt) - timestamp(right.occurredAt)
    || Number(left.action === 'APPLIED') - Number(right.action === 'APPLIED'));
}

function plainValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '없음';
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

function dateValue(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) return value;
  return `${value.slice(0, 4)}. ${value.slice(5, 7)}. ${value.slice(8, 10)}.`;
}

function numberValue(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 20 }).format(value);
  if (typeof value === 'string' && /^-?\d+(?:\.\d+)?$/.test(value)) {
    const [integer, fraction] = value.split('.');
    return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction === undefined ? '' : `.${fraction}`}`;
  }
  return undefined;
}

/** 감사 기록에 포함된 실제 값과 현재 제공받은 라벨만 사용합니다. 통화·참조 이름을 추측하지 않습니다. */
export function formatWorkflowFieldValue(field: WorkflowFieldDefinition, value: unknown, context?: WorkflowActivityValueContext): string {
  if (field.type === 'DISPLAY_TEXT' && typeof field.config?.content === 'string') return field.config.content;
  if (value === undefined || value === null || value === '') return '없음';
  if (field.type === 'NUMBER' || field.type === 'CURRENCY') {
    const formatted = numberValue(value);
    if (formatted !== undefined) {
      const currency = field.type === 'CURRENCY' && typeof field.config?.currency === 'string' ? field.config.currency.trim() : '';
      return currency ? `${currency} ${formatted}` : formatted;
    }
  }
  if (field.type === 'DATE' && typeof value === 'string') return dateValue(value);
  if (field.type === 'DATE_RANGE' && typeof value === 'object' && !Array.isArray(value)) {
    const range = value as Record<string, unknown>;
    if (Object.keys(range).every((key) => key === 'from' || key === 'to') && (typeof range.from === 'string' || typeof range.to === 'string')
      && (range.from === undefined || typeof range.from === 'string') && (range.to === undefined || typeof range.to === 'string')) {
      return `${typeof range.from === 'string' && range.from ? dateValue(range.from) : '없음'} ~ ${typeof range.to === 'string' && range.to ? dateValue(range.to) : '없음'}`;
    }
  }
  if (field.type === 'SINGLE_SELECT' || field.type === 'MULTI_SELECT') {
    const options = Array.isArray(field.config?.options) ? field.config.options : [];
    const label = (selected: string) => {
      const option = options.find((item) => item && typeof item === 'object' && item.value === selected);
      return option && typeof option.label === 'string' ? option.label : selected;
    };
    if (typeof value === 'string') return label(value);
    if (Array.isArray(value) && value.every((item) => typeof item === 'string')) return value.length ? value.map(label).join(', ') : '없음';
  }
  if (field.type === 'PERSON' && typeof value === 'string') return context?.people?.find((person) => person.personId === value)?.name || value;
  if (field.type === 'ORGANIZATION' && typeof value === 'string') return context?.organizations?.find((organization) => organization.id === value)?.name || value;
  return plainValue(value);
}

export function formatWorkflowActivityValue(change: WorkflowActivityChange, value: unknown, context?: WorkflowActivityValueContext): string {
  const key = change.fieldKey ?? (change.path.startsWith('values.') ? change.path.slice('values.'.length) : undefined);
  const field = context?.fields?.find((item) => item.key === key);
  return field ? formatWorkflowFieldValue(field, value, context) : plainValue(value);
}
